import React from 'react';
import { Box, Text, VStack, HStack, Icon, Button, OrderedList, ListItem, Code, Badge } from '@chakra-ui/react';
import { FiCheckCircle, FiClock, FiRefreshCw, FiRadio, FiAlertTriangle } from 'react-icons/fi';
import Card from 'components/card/Card';
import { CopyField, ago } from './_shared';

/**
 * The two values a tenant types into their own Meta app, and the proof they
 * worked.
 *
 * Both facts come from the connection layer, which is what Meta talks to: the
 * handshake stamps `handshakeVerifiedAt` and the first signed delivery stamps
 * `lastSignedEventAt`. Neither is guessable from the CRM, and both are exactly
 * what an administrator stares at while clicking "Verify and save" in Meta.
 */
export default function WebhookCard({ webhook, onRefresh, isRefreshing }) {
	const verified = Boolean(webhook?.handshakeVerifiedAt);
	const receiving = Boolean(webhook?.lastSignedEventAt);

	// The server withholds `callbackUrl` when the address it resolved is one
	// Meta cannot call, and sends the reason instead. Two different sentences,
	// because the two fixes are different: set a variable, versus publish the
	// service somewhere public.
	const unreachable = Boolean(webhook?.publicUrlConfigured) && !webhook?.publicUrlReachable;
	const callbackHint = unreachable
		? `Not usable yet: ${webhook.publicUrlReason}. Publish the connection service at a public HTTPS address and set WA_PUBLIC_BASE_URL to it.`
		: 'Not available yet: the platform operator must set WA_PUBLIC_BASE_URL on the connection service, or CRM_PRODUCT_API_URL to a public HTTPS address.';

	return (
		<Card bg="bg.surface" borderWidth="1px" borderColor="border.default">
			<Box>
				<HStack justify="space-between" align="start" mb="3">
					<Box>
						<HStack spacing="2">
							<Badge colorScheme="gray" variant="subtle" fontSize="0.55rem">
								Last step
							</Badge>
							<Text fontSize="sm" fontWeight="bold" color="text.primary">
								Let customers reply to you
							</Text>
						</HStack>
						{/* ★ SAYS WHAT IT IS *NOT* NEEDED FOR, FIRST.
						    Titled "Point Meta at this CRM" and sitting above the numbers, this
						    read as the next thing to do, so people stopped here waiting on a
						    public HTTPS address before testing anything. Sending works without
						    it entirely. Naming the half it unblocks stops it looking like a
						    blocker for the half it does not. */}
						<Text fontSize="xs" color="text.muted" mt="0.5">
							Sending already works without this. These two values are what make replies
							from customers arrive in your inbox — paste them into your Meta app when you are ready.
						</Text>
					</Box>
					<Button
						size="xs"
						variant="ghost"
						leftIcon={<Icon as={FiRefreshCw} />}
						onClick={onRefresh}
						isLoading={isRefreshing}
					>
						Check status
					</Button>
				</HStack>

				<VStack align="stretch" spacing="3">
					{unreachable ? (
						<HStack
							spacing="2"
							align="start"
							bg="bg.subtle"
							borderWidth="1px"
							borderColor="warning"
							borderRadius="md"
							p="3"
						>
							<Icon as={FiAlertTriangle} color="warning" mt="0.5" />
							<Text fontSize="xs" color="text.secondary">
								There is no address Meta can reach yet, so there is nothing to paste below.
								 Incoming messages cannot arrive until the connection service is published at a
								 public HTTPS address.
							</Text>
						</HStack>
					) : null}
					<CopyField
						label="Callback URL"
						value={webhook?.callbackUrl}
						hint={callbackHint}
					/>
					<CopyField label="Verify token" value={webhook?.verifyToken} hint="Loading…" />

					<Box bg="bg.subtle" borderRadius="md" p="3">
						<Text fontSize="xs" fontWeight="medium" color="text.secondary" mb="1.5">
							In your Meta app
						</Text>
						<OrderedList fontSize="xs" color="text.secondary" spacing="1" pl="4">
							<ListItem>
								Open <Code fontSize="xs">WhatsApp → Configuration</Code> and click <b>Edit</b> under Webhook.
							</ListItem>
							<ListItem>Paste the Callback URL and the Verify token, then click <b>Verify and save</b>.</ListItem>
							<ListItem>
								Under <b>Webhook fields</b>, click <b>Manage</b> and subscribe to <Code fontSize="xs">messages</Code>.
							</ListItem>
						</OrderedList>
					</Box>

					<VStack align="stretch" spacing="1.5">
						<HStack spacing="2">
							<Icon as={verified ? FiCheckCircle : FiClock} color={verified ? 'success' : 'text.muted'} />
							<Text fontSize="sm" color={verified ? 'text.primary' : 'text.muted'}>
								{verified
									? `Meta verified your webhook ${ago(webhook.handshakeVerifiedAt)}.`
									: 'Waiting for Meta to verify the webhook — click "Verify and save" in your app.'}
							</Text>
						</HStack>
						<HStack spacing="2">
							<Icon as={receiving ? FiRadio : FiClock} color={receiving ? 'success' : 'text.muted'} />
							<Text fontSize="sm" color={receiving ? 'text.primary' : 'text.muted'}>
								{receiving
									? `Last message from Meta received ${ago(webhook.lastSignedEventAt)}.`
									: 'No messages received yet. Send a WhatsApp message to your number to test it.'}
							</Text>
						</HStack>
					</VStack>
				</VStack>
			</Box>
		</Card>
	);
}
